import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type KnowledgeBaseAccessJSON = z.infer<typeof KnowledgeBaseAccessSchema.KnowledgeBaseAccessJSONZ>;

export type KnowledgeBaseAccessParams = z.infer<typeof KnowledgeBaseAccessSchema.KnowledgeBaseAccessZ>;

export default class KnowledgeBaseAccessSchema {
  public static readonly KnowledgeBaseAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.KnowledgeBaseAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly KnowledgeBaseAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.KnowledgeBaseAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): KnowledgeBaseAccessParams {
    return parseOrThrow(KnowledgeBaseAccessSchema.KnowledgeBaseAccessZ, values, {
      logError: true,
      schemaName: "KnowledgeBaseAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): KnowledgeBaseAccessJSON {
    return parseOrThrow(KnowledgeBaseAccessSchema.KnowledgeBaseAccessJSONZ, values, {
      logError: true,
      schemaName: "KnowledgeBaseAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is KnowledgeBaseAccessParams {
    try {
      KnowledgeBaseAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is KnowledgeBaseAccessJSON {
    try {
      KnowledgeBaseAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: KnowledgeBaseAccessJSON): KnowledgeBaseAccessParams {
    const parsed = parseOrThrow(KnowledgeBaseAccessSchema.KnowledgeBaseAccessJSONZ, data, {
      logError: true,
      schemaName: "KnowledgeBaseAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return KnowledgeBaseAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: KnowledgeBaseAccessParams): KnowledgeBaseAccessJSON {
    const parsed = KnowledgeBaseAccessSchema.validate(notification);
    return KnowledgeBaseAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    KnowledgeBaseAccessSchema.KnowledgeBaseAccessJSONZ,
    KnowledgeBaseAccessSchema.KnowledgeBaseAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.KnowledgeBaseAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.KnowledgeBaseAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
